import { useCallback, useEffect, useState } from 'react';
import { storage } from '@shared/utils/storage';

/**
 * `useState` backed by localStorage, with cross-tab synchronization via the
 * `storage` event. Reads/writes go through the fail-soft `storage` helper.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): readonly [T, (value: T | ((prev: T) => T)) => void] {
  const [stored, setStored] = useState<T>(() => storage.get<T>(key) ?? initialValue);

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStored((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        storage.set(key, next);
        return next;
      });
    },
    [key],
  );

  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === key && event.newValue) {
        try {
          setStored(JSON.parse(event.newValue) as T);
        } catch {
          /* ignore malformed cross-tab payloads */
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [key]);

  return [stored, setValue] as const;
}
